'use client';

import { useMemoizedFn, useSetState } from 'ahooks';
import { useTranslations } from 'next-intl';
import { type FC, useCallback, useState } from 'react';
import { AgentLineGenerateVideo } from '@/app/components/icons/AgentLineGenerateVideo';
import { BaseLineFaceswap } from '@/app/components/icons/BaseLineFaceswap';
import { MediaDropZone } from '@/app/components/shared/MediaUploader/MediaDropZone';
import { MAX_IMAGE_FILE_SIZE, MAX_VIDEO_FILE_SIZE, SITE } from '@/app/constants';
import { useDevice, useFetchRecreateData, useGlobalModal, useRunTempUploadBatch } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type {
  CreationWithTask,
  FormImagePrompt,
  ImageUploadFile,
  TaskInfo,
  ToolTaskFormVideoPrompt,
  VideoUploadFile,
} from '@/app/types';
import { isExtendTask, isVideoDurationWithinRange, normalizeDiscreteVideoDurationToSeconds } from '@/app/utils';
import { CreationSelectorDialog } from '../_components/CreationSelectorDialog';
import { ExampleMediaPreview } from '../_components/ExampleMediaPreview';
import { ToolFormContainer } from '../_components/ToolFormContainer';
import { ToolFormSubmitButton } from '../_components/ToolFormSubmitButton';
import { ToolMediaUpload } from '../_components/ToolMediaUpload';
import { ToolMediaUploadHeader } from '../_components/ToolMediaUploadHeader';
import { useCreationConsent } from '../_hooks/use-creation-consent';
import { useToolForm } from '../_hooks/use-tool-form';
import {
  applyToolFormParamsPatch,
  buildToolTaskRecreatePatch,
  createImagePromptFromCreation,
  createPromptListHelpers,
  // createImagePromptFromCreation,
  createToolImagePromptFromUploadFile,
  createToolVideoPromptFromUploadFile,
  createVideoPromptFromCreation,
} from '../_utils';
import { getToolTempImagesPendingItems, getToolTempVideosPendingItems } from '../_utils/tool-temp-upload-items';

const maxVideoSize = MAX_VIDEO_FILE_SIZE;
const maxVideoDuration = 8;
const minVideoDuration = 1;

interface FaceSwapState {
  selectedFaceCreation?: CreationWithTask;
  selectedVideoCreation?: CreationWithTask;
}

const FaceSwapForm: FC = () => {
  const t = useTranslations();
  const { isMobile } = useDevice();
  const storeApi = useStoreApi();

  const { updateSettings } = useToolForm('face_swap');

  const [openFaceSelector, setOpenFaceSelector] = useState(false);
  const [openVideoSelector, setOpenVideoSelector] = useState(false);
  const [selectedFaceCreationIndex, setSelectedFaceCreationIndex] = useState<number | null>(null);
  const [selectedVideoCreationIndex, setSelectedVideoCreationIndex] = useState<number | null>(null);

  const [faceSwapState, setFaceSwapState] = useSetState<FaceSwapState>({
    selectedFaceCreation: undefined,
    selectedVideoCreation: undefined,
  });

  const { open: openSimpleCreationDetailModal } = useGlobalModal('simpleCreationDetailModal');

  const { updateFaceSwapFormParams, imagePrompts, videoPrompts, userInfo } = useStore((state) => ({
    updateFaceSwapFormParams: state.updateFaceSwapFormParams,
    imagePrompts: state.faceSwapFormParams.imagePrompts,
    videoPrompts: state.faceSwapFormParams.videoPrompts,
    userInfo: state.userInfo,
  }));

  const faceImagePrompt = imagePrompts?.[0] || null;
  const videoPrompt = videoPrompts?.[0] || null;
  const ensureFaceConsent = useCreationConsent(userInfo?.id, { requireFaceConsent: SITE === 'china' });

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload temp images',
    (refs) =>
      getToolTempImagesPendingItems(
        refs,
        createPromptListHelpers(
          () => storeApi.getState().faceSwapFormParams.imagePrompts,
          (prompts) => updateFaceSwapFormParams('imagePrompts', prompts.filter(Boolean) as FormImagePrompt[]),
        ),
        { imagePrompts },
      ),
    [imagePrompts, updateFaceSwapFormParams, userInfo],
  );

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload temp videos',
    (refs) =>
      getToolTempVideosPendingItems(
        refs,
        createPromptListHelpers(
          () => storeApi.getState().faceSwapFormParams.videoPrompts,
          (prompts) => updateFaceSwapFormParams('videoPrompts', prompts.filter(Boolean) as ToolTaskFormVideoPrompt[]),
        ),
        { videoPrompts },
      ),
    [updateFaceSwapFormParams, userInfo, videoPrompts],
  );

  const handleTaskSuccess = useCallback(
    async (task: TaskInfo, inspirationId?: string, source?: string) => {
      const patch = await buildToolTaskRecreatePatch({
        tool: 'face_swap',
        task,
        inspirationId,
        inspirationFrom: source,
      });
      applyToolFormParamsPatch(updateFaceSwapFormParams, patch);
    },
    [updateFaceSwapFormParams],
  );

  useFetchRecreateData({
    onTaskSuccess: handleTaskSuccess,
  });

  const handleSelectFaceImage = useCallback(
    (index: number | null, creation?: CreationWithTask) => {
      if (!creation) {
        setSelectedFaceCreationIndex(null);
        setFaceSwapState({
          selectedFaceCreation: undefined,
        });
        return;
      }

      setSelectedFaceCreationIndex(index);
      setFaceSwapState({
        selectedFaceCreation: creation,
      });
    },
    [setFaceSwapState],
  );

  const handleSelectVideo = useCallback(
    (index: number | null, creation?: CreationWithTask) => {
      if (!creation) {
        setSelectedVideoCreationIndex(null);
        setFaceSwapState({
          selectedVideoCreation: undefined,
        });
        return;
      }

      setSelectedVideoCreationIndex(index);
      setFaceSwapState({
        selectedVideoCreation: creation,
      });
    },
    [setFaceSwapState],
  );

  const handleRemoveVideo = useCallback(() => {
    updateFaceSwapFormParams('videoPrompts', []);
  }, [updateFaceSwapFormParams]);

  const handleRemoveFaceImage = useCallback(() => {
    updateFaceSwapFormParams('imagePrompts', []);
  }, [updateFaceSwapFormParams]);

  const handleVideoUpload = useMemoizedFn((file: VideoUploadFile) => {
    const videoPrompt = createToolVideoPromptFromUploadFile(file);
    updateFaceSwapFormParams('videoPrompts', [videoPrompt]);
    updateSettings({ duration: normalizeDiscreteVideoDurationToSeconds(file.file?.duration) });

    if (file.status === 'done' && file.file) {
      // updateFaceSwapFormParams('creationId', file.id || '');
    }

    if (file.status === 'error' || file.status === 'removed') {
      updateFaceSwapFormParams('videoPrompts', []);
      // updateFaceSwapFormParams('creationId', undefined);
    }
  });

  const handleFaceImageUpload = useMemoizedFn((file: ImageUploadFile) => {
    const imagePrompt = createToolImagePromptFromUploadFile(file);
    updateFaceSwapFormParams('imagePrompts', [imagePrompt]);

    if (file.status === 'done' && file.file) {
      // const faceImageUrl = 'selectedImagePreview' in file.file ? file.file.selectedImagePreview : '';
      // updateSettings({ faceImageUrl });
    }

    if (file.status === 'error' || file.status === 'removed') {
      updateFaceSwapFormParams('imagePrompts', []);
      // updateSettings({ faceImageUrl: '' });
    }
  });

  const getSelectedImage = useCallback(async (url: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = URL.createObjectURL(blob);
      });
      return { blob, width: img.width, height: img.height };
    } catch {
      return null;
    }
  }, []);

  const hasUploadingMedia =
    imagePrompts.some((prompt) => prompt.status === 'uploading') ||
    videoPrompts.some((prompt) => prompt.status === 'uploading');
  const isSubmitDisabled =
    hasUploadingMedia ||
    !videoPrompt ||
    videoPrompt.status !== 'done' ||
    !faceImagePrompt ||
    faceImagePrompt.status !== 'done';

  const remainingVideoSlots = videoPrompt ? 0 : 1;
  const remainingImageSlots = faceImagePrompt ? 0 : 1;

  return (
    <>
      <ToolFormContainer
        title={t('tools.faceSwap.title')}
        description={t('tools.faceSwap.description')}
        exampleSection={<ExampleMediaPreview tool="face_swap" />}
        mediaSection={
          <MediaDropZone
            acceptVideo={true}
            maxVideoCount={remainingVideoSlots}
            maxVideoSize={maxVideoSize}
            maxVideoDuration={maxVideoDuration}
            minVideoDuration={minVideoDuration}
            maxImageCount={remainingImageSlots}
            disabled={remainingVideoSlots === 0 && remainingImageSlots === 0}
            maxImageFileSize={MAX_IMAGE_FILE_SIZE}
            getSelectedImage={getSelectedImage}
            allowTempMode={true}
            enablePaste={true}
            onChange={(file, type) => {
              if (type === 'video') handleVideoUpload(file as VideoUploadFile);
              else if (type === 'image') handleFaceImageUpload(file as ImageUploadFile);
            }}
          >
            <div className="flex w-full gap-3 sm:flex-col">
              <ToolMediaUpload
                type="video"
                header={
                  <ToolMediaUploadHeader
                    icon={<AgentLineGenerateVideo className="size-5 text-system-white" />}
                    title={t('tools.targetVideo')}
                  />
                }
                videoPrompt={videoPrompt}
                isMobile={isMobile}
                className="flex-1"
                maxVideoSize={maxVideoSize}
                maxDuration={maxVideoDuration}
                minDuration={minVideoDuration}
                onVideoUpload={handleVideoUpload}
                onOpenSelector={() => setOpenVideoSelector(true)}
                onRemoveMedia={handleRemoveVideo}
              />
              <ToolMediaUpload
                type="image"
                header={
                  <ToolMediaUploadHeader
                    icon={<BaseLineFaceswap className="size-5 text-system-white" />}
                    title={t('tools.faceSwap.sourceImage.label')}
                  />
                }
                className="flex-1"
                imagePrompt={faceImagePrompt}
                isMobile={isMobile}
                showImageWatermark={
                  Boolean(faceImagePrompt?.reference?.creation_id) &&
                  (SITE === 'china' || userInfo?.subs_plan === 'free')
                }
                maxFileSize={MAX_IMAGE_FILE_SIZE}
                getSelectedImage={getSelectedImage}
                onImageUpload={handleFaceImageUpload}
                onOpenSelector={() => setOpenFaceSelector(true)}
                onRemoveMedia={handleRemoveFaceImage}
              />
            </div>
          </MediaDropZone>
        }
        submitButton={
          <ToolFormSubmitButton
            taskType="face_swap"
            disabled={isSubmitDisabled}
            disabledTip={hasUploadingMedia ? t('create.createForm.error.uploadingMedia') : undefined}
            onBeforeSubmit={ensureFaceConsent}
          >
            {t('create.createForm.submit')}
          </ToolFormSubmitButton>
        }
      />

      <CreationSelectorDialog
        open={openFaceSelector}
        onOpenChange={setOpenFaceSelector}
        selectedIndex={selectedFaceCreationIndex}
        mediaType="image"
        onSelectionChange={handleSelectFaceImage}
        onConfirm={() => {
          setOpenFaceSelector(false);
          const selectedCreation = faceSwapState.selectedFaceCreation;
          if (selectedCreation) {
            updateFaceSwapFormParams('imagePrompts', [
              createImagePromptFromCreation(selectedCreation, { includeReference: true }),
            ]);
          }
        }}
        onViewCreation={(creation) => {
          openSimpleCreationDetailModal({
            creation,
          });
        }}
      />

      <CreationSelectorDialog
        open={openVideoSelector}
        onOpenChange={setOpenVideoSelector}
        selectedIndex={selectedVideoCreationIndex}
        mediaType="video"
        maxDuration={maxVideoDuration}
        minDuration={minVideoDuration}
        onSelectionChange={handleSelectVideo}
        onConfirm={() => {
          setOpenVideoSelector(false);
          const selectedCreation = faceSwapState.selectedVideoCreation;
          const selectedDuration =
            (isExtendTask(selectedCreation?.task.type)
              ? selectedCreation?.duration
              : selectedCreation?.task.settings.duration) || 0;
          if (
            selectedCreation &&
            isVideoDurationWithinRange({
              duration: selectedDuration,
              minDuration: minVideoDuration,
              maxDuration: maxVideoDuration,
            })
          ) {
            const videoPrompt = createVideoPromptFromCreation(selectedCreation, {
              includeReference: true,
            });
            updateFaceSwapFormParams('videoPrompts', [videoPrompt]);
            updateSettings({ duration: normalizeDiscreteVideoDurationToSeconds(selectedDuration) });
            // updateFaceSwapFormParams('creationId', selectedCreation.id);
          }
        }}
        onViewCreation={(creation) => {
          openSimpleCreationDetailModal({
            creation,
          });
        }}
      />
    </>
  );
};

export default FaceSwapForm;
